import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import Tooltip from './Tooltip';
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  EyeSlashIcon,
  EyeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  CheckIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/solid';
import { config } from '../config/environment';
import { AppointmentType, APPOINTMENT_TYPE_BADGES } from '../constants/appointmentTypes';
import { endpoints } from '../config/endpoints';
import type { AppointmentSummary, ClientSection } from '../services/pracmagicTypes';
import { Spinner, EmptyState } from './ui';
import { EF_THEME_COLORS } from '../constants/uiConstants';
import ActionItemsPanel from './ActionItemsPanel';

interface Props {
  appointmentId: string;
  meetingName: string;
  onClose: () => void;
  role?: 'coach' | 'client';
  clientName?: string;
  coachClientId?: string;
  onNavigateToCheckIns?: () => void;
  appointmentStatus?: string;
  appointmentType?: AppointmentType;
  barterNote?: string;
}

type Tab = 'prep' | 'overview' | 'action_items' | 'nudges' | 'coach_notes' | 'ef_themes' | 'client_notes';

interface PrepContent {
  client_snapshot: string;
  recent_work: string;
  client_patterns: string;
  focus_for_session: string;
}
type PrepAnnotations = Partial<Record<keyof PrepContent, string>>;
interface SessionPrepData {
  id: string;
  appointment_id: string;
  status: string;
  content: PrepContent | null;
  coach_annotations: PrepAnnotations;
}
const PREP_SECTIONS: { key: keyof PrepContent; label: string }[] = [
  { key: 'client_snapshot', label: 'Client Snapshot' },
  { key: 'recent_work', label: 'Recent Work' },
  { key: 'focus_for_session', label: 'Focus for this Session' },
];

const PrepAnnotationField: React.FC<{ value: string; onSave: (v: string) => void; saving: boolean }> = ({ value, onSave, saving }) => {
  const [draft, setDraft] = React.useState(value);
  const [editing, setEditing] = React.useState(false);
  React.useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  if (!editing && !draft) return (
    <button onClick={() => setEditing(true)} className="text-xs text-gray-400 hover:text-violet-600 flex items-center gap-1 mt-1">
      <PencilIcon className="w-3 h-3" /> Add note
    </button>
  );
  if (!editing) return (
    <div className="flex items-start gap-2 mt-2">
      <p className="text-xs text-gray-600 italic flex-1 bg-violet-50 border border-violet-100 rounded px-2 py-1.5">{draft}</p>
      <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-violet-600 mt-0.5 flex-shrink-0"><PencilIcon className="w-3.5 h-3.5" /></button>
    </div>
  );
  return (
    <div className="mt-2 space-y-1.5">
      <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={2} autoFocus
        placeholder="Your note for this section…"
        className="w-full text-xs text-gray-700 border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-1 focus:ring-violet-500 resize-none" />
      <div className="flex gap-2 justify-end">
        <button onClick={() => { setEditing(false); setDraft(value); }} className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-500">Cancel</button>
        <button onClick={() => { onSave(draft); setEditing(false); }} disabled={saving} className="text-xs px-2 py-1 rounded bg-violet-600 text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
};

const ALL_EF_DOMAINS = Object.keys(EF_THEME_COLORS);

function qualityBadge(judge: any): { label: string; className: string } | null {
  if (!judge) return null;
  const scores = [
    judge.accuracy,
    judge.ef_relevance,
    judge.action_items_score,
    judge.coach_notes_score,
    judge.hallucination_score,
  ].filter((s) => typeof s === 'number');
  if (scores.length === 0) return null;
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  if (avg >= 4.0) return { label: `Quality ${avg.toFixed(1)}/5`, className: 'bg-green-100 text-green-800' };
  if (avg >= 3.0) return { label: `Quality ${avg.toFixed(1)}/5`, className: 'bg-yellow-100 text-yellow-800' };
  return { label: `Quality ${avg.toFixed(1)}/5`, className: 'bg-red-100 text-red-800' };
}

const Tip: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="relative group/tip">
    {children}
    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 rounded bg-gray-800 px-2 py-0.5 text-xs text-white whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-20">
      {label}
    </span>
  </div>
);

// Maps markdown heading text → built-in section key (matches CoachPromptSettingsPanel labels)
const HEADING_TO_KEY: Record<string, string> = {
  'Session Overview':              'session_overview',
  'Wins':                          'wins',
  'Session Agreement':             'agreement',
  'Key Topics Discussed':          'key_topics',
  'EF / ADHD Patterns Observed':   'ef_patterns',
  'Client Insights & Breakthroughs': 'insights',
  'Coaching Approach Used':        'coaching_approach',
  'Client Strengths Demonstrated': 'client_strengths',
  'Current Challenges & Obstacles': 'challenges',
};

interface VisibilityBadgeProps { headingText: string; clientHidden: string[]; customSections: Array<{ display_name: string; client_visible: boolean }> }
function VisibilityBadge({ headingText, clientHidden, customSections }: VisibilityBadgeProps) {
  const builtInKey = HEADING_TO_KEY[headingText];
  let isHidden = false;
  if (builtInKey) {
    isHidden = clientHidden.includes(builtInKey);
  } else {
    const custom = customSections.find(c => c.display_name === headingText);
    if (custom) isHidden = !custom.client_visible;
    else return null;
  }
  return isHidden ? (
    <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-400 border border-gray-200 align-middle">
      <EyeSlashIcon className="w-3 h-3" /> Coach only
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-50 text-sky-600 border border-sky-200 align-middle">
      <EyeIcon className="w-3 h-3" /> Visible to client
    </span>
  );
}

interface SummaryMarkdownProps { text: string; clientHidden?: string[]; customSections?: Array<{ display_name: string; client_visible: boolean }> }
const SummaryMarkdown: React.FC<SummaryMarkdownProps> = ({ text, clientHidden = [], customSections = [] }) => {
  const showVisibility = clientHidden.length > 0 || customSections.length > 0;
  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <h1 className="text-base font-bold text-gray-900 mt-5 mb-2 first:mt-0">{children}</h1>
        ),
        h2: ({ children }) => {
          const label = typeof children === 'string' ? children : Array.isArray(children) ? children.join('') : '';
          return (
            <h2 className="text-sm font-bold text-green-600 mt-5 mb-2 first:mt-0 flex items-center flex-wrap gap-1">
              <span>{children}</span>
              {showVisibility && <VisibilityBadge headingText={label} clientHidden={clientHidden} customSections={customSections} />}
            </h2>
          );
        },
        h3: ({ children }) => {
          const label = typeof children === 'string' ? children : Array.isArray(children) ? children.join('') : '';
          return (
            <h3 className="text-sm font-semibold text-green-600 mt-4 mb-1.5 first:mt-0 flex items-center flex-wrap gap-1">
              <span>{children}</span>
              {showVisibility && <VisibilityBadge headingText={label} clientHidden={clientHidden} customSections={customSections} />}
            </h3>
          );
        },
        p: ({ children }) => (
          <p className="text-sm text-gray-700 leading-relaxed mb-3">{children}</p>
        ),
        ul: ({ children }) => (
          <ul className="space-y-1 mb-3 pl-1">{children}</ul>
        ),
        li: ({ children }) => (
          <li className="flex gap-2 text-sm text-gray-700 leading-relaxed">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
            <span>{children}</span>
          </li>
        ),
        strong: ({ children }) => (
          <strong className="font-semibold text-gray-900">{children}</strong>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

const SessionSummaryPanel: React.FC<Props> = ({
  appointmentId,
  meetingName,
  onClose,
  role = 'coach',
  clientName,
  coachClientId,
  onNavigateToCheckIns,
  appointmentStatus,
  appointmentType: initialAppointmentType,
  barterNote: initialBarterNote,
}) => {
  const isCoach = role === 'coach';
  const isUpcoming = appointmentStatus === 'confirmed' || appointmentStatus === 'scheduled';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AppointmentSummary | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>(isCoach && isUpcoming ? 'prep' : 'overview');

  // ── Session Prep state ────────────────────────────────────────────────────
  const [prep, setPrep] = useState<SessionPrepData | null>(null);
  const [prepLoading, setPrepLoading] = useState(false);
  const [prepGenerating, setPrepGenerating] = useState(false);
  const [prepAnnotations, setPrepAnnotations] = useState<PrepAnnotations>({});
  const [prepAnnotationSaving, setPrepAnnotationSaving] = useState<string | null>(null);
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
  const [evaluation, setEvaluation] = useState<any>(null);
  const [rating, setRating] = useState<1 | -1 | null>(null);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingConfirmed, setRatingConfirmed] = useState(false);
  const [approvedNotes, setApprovedNotes] = useState<boolean>(false);
  const [approvingNotes, setApprovingNotes] = useState(false);
  const [sendingNotes, setSendingNotes] = useState(false);
  const [notesSent, setNotesSent] = useState(false);
  const [sendNotesError, setSendNotesError] = useState<string | null>(null);

  // ── Client visibility config (coach only) ────────────────────────────────
  const [clientHidden, setClientHidden] = useState<string[]>([]);
  const [customSections, setCustomSections] = useState<Array<{ display_name: string; client_visible: boolean }>>([]);

  useEffect(() => {
    if (!isCoach) return;
    fetch(`${config.apiBaseUrl}${endpoints.main.coaches.promptSettings}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.sections_config) return;
        const sc = d.sections_config;
        setClientHidden(Array.isArray(sc.client_hidden) ? sc.client_hidden : []);
        setCustomSections(Array.isArray(sc.custom) ? sc.custom : []);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoach]);

  // ── Reprocess state ───────────────────────────────────────────────────────
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessError, setReprocessError] = useState<string | null>(null);
  const [showReprocessConfirm, setShowReprocessConfirm] = useState(false);

  // ── Appointment type state ────────────────────────────────────────────────
  const [apptType, setApptType] = useState<AppointmentType>(initialAppointmentType ?? 'billable');
  const [apptBarterNote, setApptBarterNote] = useState(initialBarterNote ?? '');
  const [typeEditing, setTypeEditing] = useState(false);
  const [typeDraft, setTypeDraft] = useState<AppointmentType>(initialAppointmentType ?? 'billable');
  const [typeBarterDraft, setTypeBarterDraft] = useState(initialBarterNote ?? '');
  const [typeSaving, setTypeSaving] = useState(false);
  const [typeError, setTypeError] = useState('');
  const [showTypeConfirm, setShowTypeConfirm] = useState(false);

  const authHeader = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    'Content-Type': 'application/json',
  }), []);

  // ── Load summary + evaluation ─────────────────────────────────────────────

  const fetchSummaryData = useCallback(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };

    const fetchSummary = fetch(
      `${config.apiBaseUrl}${endpoints.main.session.summary(appointmentId)}`,
      { headers }
    ).then(async (res) => {
      if (res.status === 404) {
        const data = await res.json();
        setError(data.detail || 'No summary available for this session');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSummary(data.summary ?? null);
      setApprovedNotes(data.summary?.coach_approved ?? false);
    });

    const fetches: Promise<any>[] = [fetchSummary];

    if (isCoach) {
      const fetchEval = fetch(
        `${config.apiBaseUrl}${endpoints.main.session.evaluate(appointmentId)}`,
        { headers }
      ).then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        setEvaluation(data);
        setRating(data?.coach_rating?.rating ?? null);
      }).catch(() => {});
      fetches.push(fetchEval);
    }

    Promise.all(fetches)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load summary'))
      .finally(() => setLoading(false));
  }, [appointmentId, isCoach]);

  // Initial load
  useEffect(() => { fetchSummaryData(); }, [fetchSummaryData]);

  // Refresh summary (including action items) whenever the user switches tabs
  const tabMountedRef = React.useRef(false);
  useEffect(() => {
    if (!tabMountedRef.current) { tabMountedRef.current = true; return; }
    fetchSummaryData();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load + poll session prep ──────────────────────────────────────────────

  const fetchPrep = useCallback(async () => {
    if (!isCoach) return;
    setPrepLoading(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.sessionPrep.get(appointmentId)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } });
      if (res.status === 404) { setPrep(null); return; }
      if (!res.ok) return;
      const data = await res.json();
      setPrep(data);
      if (data.coach_annotations) setPrepAnnotations(data.coach_annotations);
    } finally {
      setPrepLoading(false);
    }
  }, [appointmentId, isCoach]);

  useEffect(() => { fetchPrep(); }, [fetchPrep]);

  useEffect(() => {
    if (!prep || prep.status !== 'generating') return;
    const interval = setInterval(async () => {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.sessionPrep.get(appointmentId)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } });
      if (!res.ok) return;
      const data = await res.json();
      setPrep(data);
      if (data.status !== 'generating') {
        if (data.coach_annotations) setPrepAnnotations(data.coach_annotations);
        clearInterval(interval);
      }
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prep?.status, appointmentId]); // prep.status is intentional — only re-run when status changes, not on every re-render

  const doGeneratePrep = async () => {
    setShowRegenerateWarning(false);
    setPrepGenerating(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.sessionPrep.generate(appointmentId)}`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } });
      if (!res.ok) return;
      const data = await res.json();
      setPrep(data);
    } finally {
      setPrepGenerating(false);
    }
  };

  const saveAnnotation = async (key: keyof PrepAnnotations, value: string) => {
    setPrepAnnotationSaving(key);
    const next = { ...prepAnnotations, [key]: value || undefined };
    setPrepAnnotations(next);
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.sessionPrep.updateAnnotations(appointmentId)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ coach_annotations: next }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.coach_annotations) setPrepAnnotations(data.coach_annotations);
      }
    } finally {
      setPrepAnnotationSaving(null);
    }
  };

  // ── Save summary fields ───────────────────────────────────────────────────

  const saveSummaryFields = useCallback(async (fields: Record<string, any>) => {
    await fetch(`${config.apiBaseUrl}${endpoints.main.session.updateSummary(appointmentId)}`, {
      method: 'PATCH',
      headers: authHeader(),
      body: JSON.stringify(fields),
    });
  }, [appointmentId, authHeader]);

  // ── Export PDF ────────────────────────────────────────────────────────────

  const handleExportPdf = () => {
    if (!summary) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Session Notes — ${meetingName}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 720px; margin: 40px auto; color: #111; font-size: 14px; line-height: 1.6; }
    h1 { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
    .meta { color: #666; font-size: 13px; margin-bottom: 32px; }
    h2 { font-size: 13px; font-weight: 700; color: #16a34a; margin: 24px 0 6px; }
    h3 { font-size: 13px; font-weight: 600; color: #16a34a; margin: 18px 0 4px; }
    p { margin: 0 0 10px; }
    ul { margin: 0 0 10px; padding-left: 18px; }
    li { margin-bottom: 4px; }
    strong { font-weight: 600; }
    em { font-style: italic; color: #555; }
    hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>
  <h1>${meetingName}</h1>
  <p class="meta">Session Notes${clientName ? ` — ${clientName}` : ''}</p>
  <div id="content"></div>
  <script>
    // Convert markdown to HTML inline
    const md = ${JSON.stringify(summary.summary_text || '')};
    document.getElementById('content').innerHTML = md
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^---$/gm, '<hr/>')
      .replace(/\\*\\*(.+?)\\*\\*/g, '<strong>$1</strong>')
      .replace(/\\*(.+?)\\*/g, '<em>$1</em>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\\/li>\\n?)+/g, '<ul>$&</ul>')
      .replace(/\\n\\n/g, '</p><p>')
      .replace(/^(?!<[hup])/gm, '')
      .trim();
    window.onload = () => { window.print(); window.close(); };
  </script>
</body>
</html>`);
    win.document.close();
  };

  // ── Overview editing ──────────────────────────────────────────────────────

  const [editingOverview, setEditingOverview] = useState(false);
  const [overviewDraft, setOverviewDraft] = useState('');
  const [overviewSaving, setOverviewSaving] = useState(false);

  const startEditOverview = () => {
    setOverviewDraft(summary?.summary_text ?? '');
    setEditingOverview(true);
  };
  const saveOverview = async () => {
    setOverviewSaving(true);
    try {
      await saveSummaryFields({ summary_text: overviewDraft });
      if (summary) {
        setSummary(s => s ? { ...s, summary_text: overviewDraft } : s);
        setEditingOverview(false);
      } else {
        fetchSummaryData();
        setEditingOverview(false);
      }
    } finally {
      setOverviewSaving(false);
    }
  };

  // ── Coach Notes editing ───────────────────────────────────────────────────

  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const startEditNotes = () => {
    setNotesDraft(summary?.coach_notes ?? '');
    setEditingNotes(true);
  };
  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await saveSummaryFields({ coach_notes: notesDraft });
      if (summary) {
        setSummary(s => s ? { ...s, coach_notes: notesDraft } : s);
        setEditingNotes(false);
      } else {
        fetchSummaryData();
        setEditingNotes(false);
      }
    } finally {
      setNotesSaving(false);
    }
  };

  // ── EF Themes editing ─────────────────────────────────────────────────────

  const [editingThemes, setEditingThemes] = useState(false);
  const [themesDraft, setThemesDraft] = useState<string[]>([]);
  const [themesSaving, setThemesSaving] = useState(false);

  const startEditThemes = () => {
    setThemesDraft([...(summary?.ef_themes ?? [])]);
    setEditingThemes(true);
  };
  const toggleTheme = (theme: string) => {
    setThemesDraft(prev =>
      prev.includes(theme) ? prev.filter(t => t !== theme) : [...prev, theme]
    );
  };
  const saveThemes = async () => {
    setThemesSaving(true);
    try {
      await saveSummaryFields({ ef_themes: themesDraft });
      if (summary) {
        setSummary(s => s ? { ...s, ef_themes: themesDraft } : s);
        setEditingThemes(false);
      } else {
        fetchSummaryData();
        setEditingThemes(false);
      }
    } finally {
      setThemesSaving(false);
    }
  };

  // ── Nudges ────────────────────────────────────────────────────────────────

  const [nudgeEdits, setNudgeEdits] = useState<Record<string, string>>({});
  const [nudgeEditingId, setNudgeEditingId] = useState<string | null>(null);
  const [nudgeSaving, setNudgeSaving] = useState<Record<string, boolean>>({});
  // openScheduleId: which action item has the inline schedule form open
  const [openScheduleId, setOpenScheduleId] = useState<string | null>(null);
  const [nudgeScheduleType, setNudgeScheduleType] = useState<'one_off' | 'recurring'>('one_off');
  const [nudgeScheduleDate, setNudgeScheduleDate] = useState('');
  const [nudgeScheduleDays, setNudgeScheduleDays] = useState<number[]>([]);
  const [nudgeScheduleHour, setNudgeScheduleHour] = useState(9);
  const [nudgeScheduleMinute, setNudgeScheduleMinute] = useState(0);
  const [nudgeScheduling, setNudgeScheduling] = useState<Record<string, boolean>>({});
  // Track locally-confirmed scheduled state per action item id
  const [nudgeScheduled, setNudgeScheduled] = useState<Record<string, string>>({});

  function nudgeMinDate() { return new Date().toISOString().split('T')[0]; }
  function nudgeMaxDate() {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().split('T')[0];
  }

  function openNudgeSchedule(item: any) {
    setNudgeEdits(p => ({ ...p, [item.id]: p[item.id] ?? item.nudge_context ?? '' }));
    setNudgeScheduleType('one_off');
    setNudgeScheduleDate('');
    setNudgeScheduleDays([]);
    setNudgeScheduleHour(9);
    setNudgeScheduleMinute(0);
    setNudgeEditingId(null);
    setOpenScheduleId(item.id);
  }

  function openNudgeEdit(item: any) {
    setNudgeEdits(p => ({ ...p, [item.id]: p[item.id] ?? item.nudge_context ?? '' }));
    setOpenScheduleId(null);
    setNudgeEditingId(item.id);
  }

  async function saveNudgeMessage(item: any) {
    const message = (nudgeEdits[item.id] ?? '').trim();
    setNudgeSaving(p => ({ ...p, [item.id]: true }));
    try {
      await fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.update(item.id)}`, {
        method: 'PUT', headers: authHeader(),
        body: JSON.stringify({ nudge_context: message }),
      });
      item.nudge_context = message;
      setNudgeEditingId(null);
    } finally {
      setNudgeSaving(p => ({ ...p, [item.id]: false }));
    }
  }

  const DAY_LABELS_SHORT: Record<number, string> = {1:'Mon',2:'Tue',3:'Wed',4:'Thu',5:'Fri',6:'Sat',7:'Sun'};

  async function submitNudgeSchedule(item: any) {
    const isOneOff = nudgeScheduleType === 'one_off';
    if (!coachClientId) return;
    if (isOneOff && !nudgeScheduleDate) return;
    if (!isOneOff && nudgeScheduleDays.length === 0) return;
    setNudgeScheduling(p => ({ ...p, [item.id]: true }));
    try {
      const message = (nudgeEdits[item.id] ?? item.nudge_context ?? '').trim();
      if (!message) return;
      await fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.update(item.id)}`, {
        method: 'PUT', headers: authHeader(),
        body: JSON.stringify({ nudge_context: message }),
      });
      const body: Record<string, unknown> = {
        coach_client_id: Number(coachClientId),
        schedule_type: nudgeScheduleType,
        message,
        send_hour: nudgeScheduleHour,
        send_minute: nudgeScheduleMinute,
        action_item_id: item.id,
      };
      if (isOneOff) body.scheduled_dates = [nudgeScheduleDate];
      else body.days_of_week = nudgeScheduleDays;
      const resp = await fetch(`${config.apiBaseUrl}${endpoints.main.nudgeSchedules.create}`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        const timeStr = `${nudgeScheduleHour % 12 || 12}:${String(nudgeScheduleMinute).padStart(2, '0')} ${nudgeScheduleHour < 12 ? 'AM' : 'PM'}`;
        const label = isOneOff
          ? `${new Date(nudgeScheduleDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${timeStr}`
          : `Every ${nudgeScheduleDays.map(d => DAY_LABELS_SHORT[d]).join(', ')} · ${timeStr}`;
        setNudgeScheduled(p => ({ ...p, [item.id]: label }));
        setOpenScheduleId(null);
      }
    } finally {
      setNudgeScheduling(p => ({ ...p, [item.id]: false }));
    }
  }

  // ── Rating ────────────────────────────────────────────────────────────────

  const handleRate = async (value: 1 | -1) => {
    if (ratingLoading) return;
    const newValue = rating === value ? null : value;
    setRatingLoading(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.session.rate(appointmentId)}`,
        { method: 'POST', headers: authHeader(), body: JSON.stringify({ rating: newValue }) }
      );
      if (res.ok) {
        setRating(newValue);
        setRatingConfirmed(true);
        setTimeout(() => setRatingConfirmed(false), 2000);
      }
    } finally {
      setRatingLoading(false);
    }
  };

  // ── Approve notes ────────────────────────────────────────────────────────

  const handleApproveNotes = async () => {
    if (approvingNotes) return;
    setApprovingNotes(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.session.approveSummary(appointmentId)}`,
        { method: 'POST', headers: authHeader() }
      );
      if (res.ok) {
        const data = await res.json();
        setApprovedNotes(data.coach_approved);
      }
    } finally {
      setApprovingNotes(false);
    }
  };

  // ── Send notes to client ─────────────────────────────────────────────────

  const handleSendNotes = async () => {
    if (sendingNotes) return;
    setSendingNotes(true);
    setSendNotesError(null);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.session.sendNotesToClient(appointmentId)}`,
        { method: 'POST', headers: authHeader() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send notes');
      setNotesSent(true);
    } catch (err: any) {
      setSendNotesError(err.message || 'Failed to send notes');
    } finally {
      setSendingNotes(false);
    }
  };

  // ── Reprocess / regenerate summary ───────────────────────────────────────

  const handleReprocess = async () => {
    setShowReprocessConfirm(false);
    setReprocessing(true);
    setReprocessError(null);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.session.reprocess(appointmentId)}`,
        { method: 'POST', headers: authHeader() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reprocess failed');
      setSummary(data.summary ?? null);
      setApprovedNotes(false);
    } catch (err: any) {
      setReprocessError(err.message || 'Failed to regenerate summary');
    } finally {
      setReprocessing(false);
    }
  };

  // ── Standalone action items (used by Nudges tab when no summary exists) ────
  const [standaloneActionItems, setStandaloneActionItems] = useState<any[]>([]);

  useEffect(() => {
    if (summary || !isCoach) return;
    fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.bySession(appointmentId)}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    })
      .then(r => r.ok ? r.json() : { action_items: [] })
      .then(data => setStandaloneActionItems(data.action_items ?? []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, appointmentId, isCoach]);

  // ── Visible action items for Nudges tab ────────────────────────────────────

  const visibleActionItems = summary?.action_items
    ? isCoach
      ? summary.action_items
      : summary.action_items.filter(
          (a: any) => a.assigned_to === 'client' || a.assigned_to === 'both'
        )
    : standaloneActionItems;



  // ── Tabs ──────────────────────────────────────────────────────────────────

  const hasClientSections = isCoach && (summary?.client_sections?.length ?? 0) > 0;

  const visibleTabs: Tab[] = isCoach
    ? [
        'prep',
        'overview',
        'action_items',
        'nudges',
        'coach_notes',
        ...(hasClientSections ? (['client_notes'] as Tab[]) : []),
        'ef_themes',
      ]
    : ['overview', 'action_items', 'nudges', 'ef_themes'];

  const tabLabel: Record<Tab, string> = {
    prep: 'Session Prep',
    overview: 'Overview',
    action_items: 'Action Items',
    nudges: 'Nudges',
    coach_notes: 'Coach Notes',
    client_notes: 'Client Context',
    ef_themes: 'EF Themes',
  };

  const badge = isCoach ? qualityBadge(evaluation?.judge) : null;

  // ── Appointment type helpers ──────────────────────────────────────────────
  const apptTypeBadge = APPOINTMENT_TYPE_BADGES[apptType];

  const handleSaveType = async () => {
    if (typeDraft === 'barter' && !typeBarterDraft.trim()) {
      setTypeError('A barter note is required.');
      return;
    }
    setTypeSaving(true);
    setTypeError('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.updateSessionType(appointmentId)}`,
        {
          method: 'PATCH',
          headers: authHeader(),
          body: JSON.stringify({
            appointment_type: typeDraft,
            barter_note: typeDraft === 'barter' ? typeBarterDraft.trim() : undefined,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        setTypeError(err.detail || 'Failed to update session type.');
        return;
      }
      const data = await res.json();
      setApptType(data.appointment_type);
      setApptBarterNote(data.barter_note ?? '');
      setTypeEditing(false);
      setShowTypeConfirm(false);
    } catch {
      setTypeError('Failed to update session type.');
    } finally {
      setTypeSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
          aria-hidden="true"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative z-10 w-full max-w-2xl bg-white rounded-xl shadow-xl border overflow-hidden mx-4"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Session Summary</h3>
                <p className="text-sm text-gray-500">{meetingName}</p>
              </div>
              {badge && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                  {badge.label}
                </span>
              )}
              {apptTypeBadge && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${apptTypeBadge.className}`}>
                  {apptTypeBadge.label}
                </span>
              )}
              {/* Barter note display */}
              {apptType === 'barter' && apptBarterNote && !typeEditing && (
                <span className="text-xs text-amber-700 italic">{apptBarterNote}</span>
              )}
              {/* Coach-only: change type button */}
              {isCoach && !typeEditing && (
                <button
                  onClick={() => { setTypeDraft(apptType); setTypeBarterDraft(apptBarterNote); setTypeEditing(true); setTypeError(''); }}
                  className="text-xs text-gray-400 hover:text-violet-600 underline ml-1"
                >
                  {apptType === 'billable' ? 'Mark as Pro Bono / Barter' : 'Change type'}
                </button>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Inline type editor */}
          {isCoach && typeEditing && (
            <div className="px-6 py-3 border-b bg-gray-50 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-xs font-medium text-gray-600">Session Type:</label>
                <select
                  value={typeDraft}
                  onChange={e => setTypeDraft(e.target.value as AppointmentType)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-800 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                >
                  <option value="billable">Billable</option>
                  <option value="pro_bono">Pro Bono</option>
                  <option value="barter">Barter</option>
                  <option value="complimentary">Complimentary</option>
                </select>
                {typeDraft !== apptType && typeDraft !== 'billable' && apptType === 'billable' && !showTypeConfirm && (
                  <span className="text-xs text-amber-700">Changing from Billable will clear any pricing.</span>
                )}
              </div>
              {typeDraft === 'barter' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Barter Note <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={typeBarterDraft}
                    onChange={e => setTypeBarterDraft(e.target.value)}
                    rows={2}
                    placeholder="Describe the exchange…"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-500 resize-none"
                  />
                </div>
              )}
              {typeError && <p className="text-xs text-red-600">{typeError}</p>}
              <div className="flex gap-2">
                {typeDraft !== apptType && typeDraft !== 'billable' && apptType === 'billable' && !showTypeConfirm ? (
                  <button
                    onClick={() => setShowTypeConfirm(true)}
                    className="text-xs px-3 py-1.5 rounded bg-amber-500 text-white hover:bg-amber-600"
                  >
                    Confirm change
                  </button>
                ) : (
                  <button
                    onClick={handleSaveType}
                    disabled={typeSaving}
                    className="text-xs px-3 py-1.5 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                  >
                    {typeSaving ? 'Saving…' : 'Save'}
                  </button>
                )}
                <button
                  onClick={() => { setTypeEditing(false); setShowTypeConfirm(false); setTypeError(''); }}
                  className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Tab bar — always visible for coaches, otherwise only when summary exists */}
          {(isCoach || (!loading && !error && summary)) && (
            <div className="border-b px-6 mt-1 overflow-x-auto">
              <nav className="-mb-px flex space-x-5">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab
                        ? 'border-violet-600 text-violet-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tabLabel[tab]}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-auto">
            {/* ── Session Prep tab (coach only, independent of summary) ── */}
            {activeTab === 'prep' && isCoach && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Pre-session briefing based on recent session notes.</p>
                  {prep?.status !== 'generating' && (
                    <button onClick={() => prep?.status === 'ready' ? setShowRegenerateWarning(true) : doGeneratePrep()}
                      disabled={prepGenerating}
                      className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 flex-shrink-0">
                      {prepGenerating ? 'Requesting…' : prep ? 'Regenerate' : 'Generate Prep'}
                    </button>
                  )}
                </div>
                {showRegenerateWarning && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="font-medium text-amber-800 mb-1">Overwrite existing prep?</p>
                    <p className="text-amber-700 mb-3 text-xs">Generated content will be replaced. Your annotations will be preserved.</p>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setShowRegenerateWarning(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600">Cancel</button>
                      <button onClick={doGeneratePrep} className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white">Regenerate</button>
                    </div>
                  </div>
                )}
                {prepLoading && <Spinner size="md" className="py-12" />}
                {!prepLoading && prep?.status === 'generating' && (
                  <div className="text-center py-10">
                    <Spinner size="md" className="mb-3" />
                    <p className="text-sm text-gray-500">Generating session prep…</p>
                  </div>
                )}
                {!prepLoading && !prep && !prepGenerating && (
                  <EmptyState icon={ClipboardDocumentIcon} title="No prep yet" description="Click Generate Prep to create your pre-session briefing." />
                )}
                {!prepLoading && prep?.status === 'failed' && (
                  <EmptyState icon={ClipboardDocumentIcon} title="Generation failed" description="Something went wrong. Please try generating again." />
                )}
                {!prepLoading && prep?.status === 'ready' && prep.content && (
                  <div className="space-y-5">
                    {PREP_SECTIONS.map(({ key, label }) => {
                      const text: string = prep.content![key] || '';
                      const annotation = prepAnnotations[key] || '';
                      return (
                        <div key={key}>
                          <h4 className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-1.5">{label}</h4>
                          {text && <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{text}</p>}
                          <PrepAnnotationField value={annotation} onSave={v => saveAnnotation(key, v)} saving={prepAnnotationSaving === key} />
                          {/* Client Patterns nested under Client Snapshot */}
                          {key === 'client_snapshot' && prep.content!.client_patterns && (
                            <div className="mt-3 ml-3 pl-3 border-l-2 border-violet-200">
                              <h5 className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-1">Client Patterns</h5>
                              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{prep.content!.client_patterns}</p>
                              <PrepAnnotationField value={prepAnnotations['client_patterns'] || ''} onSave={v => saveAnnotation('client_patterns', v)} saving={prepAnnotationSaving === 'client_patterns'} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Action Items: always available regardless of summary ── */}
            {activeTab === 'action_items' && !loading && (
              <ActionItemsPanel
                mode="by_appointment"
                entityId={appointmentId}
                isCoach={isCoach}
                clientName={clientName}
              />
            )}

            {/* ── Nudges: always available for coaches regardless of summary ── */}
            {activeTab === 'nudges' && !loading && (
              <div>
                {visibleActionItems.length === 0 ? (
                  <p className="text-gray-400 italic text-sm">
                    No action items yet. Add action items first, then configure nudges here.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {visibleActionItems.map((item: any) => {
                      const currentText = nudgeEdits[item.id] ?? item.nudge_context ?? '';
                      const isScheduleOpen = openScheduleId === item.id;
                      const isEditOpen = nudgeEditingId === item.id;
                      const isScheduling = nudgeScheduling[item.id];
                      const isSaving = nudgeSaving[item.id];
                      const confirmedLabel = nudgeScheduled[item.id];
                      const existingNext = item.scheduled_nudge_next
                        ? new Date(item.scheduled_nudge_next).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                        : null;
                      const scheduledLabel = confirmedLabel || (existingNext ? `Scheduled · ${existingNext}` : null);

                      return (
                        <li key={item.id} className="border border-dark-border rounded-lg bg-dark-card">
                          <div className="flex items-start justify-between gap-3 p-3">
                            <div className="min-w-0 flex-1">
                              {currentText
                                ? <p className="text-sm font-medium text-gray-100">{currentText}</p>
                                : <p className="text-sm text-dark-muted italic">No message set</p>
                              }
                              <p className="text-xs text-dark-muted mt-0.5 truncate">Re: {item.title}</p>
                              {scheduledLabel ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-400 font-medium mt-1">
                                  <CheckIcon className="w-3 h-3" />{scheduledLabel}
                                </span>
                              ) : (
                                <span className="text-xs text-dark-muted mt-1 block">Not scheduled</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Tip label="Edit nudge message">
                                <button
                                  onClick={() => isEditOpen ? setNudgeEditingId(null) : openNudgeEdit(item)}
                                  className={`p-1.5 rounded-lg transition-colors ${isEditOpen ? 'bg-primary-900/40 text-primary-400' : 'text-gray-400 hover:text-gray-300 hover:bg-dark-hover'}`}
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                              </Tip>
                              <Tip label={scheduledLabel ? 'Reschedule nudge' : 'Schedule nudge'}>
                                <button
                                  onClick={() => isScheduleOpen ? setOpenScheduleId(null) : openNudgeSchedule(item)}
                                  className={`p-1.5 rounded-lg transition-colors ${isScheduleOpen ? 'bg-amber-900/40 text-amber-400' : scheduledLabel ? 'text-amber-500 hover:bg-amber-900/20' : 'text-gray-400 hover:text-gray-300 hover:bg-dark-hover'}`}
                                >
                                  <ClockIcon className="w-4 h-4" />
                                </button>
                              </Tip>
                            </div>
                          </div>

                          {isEditOpen && (
                            <div className="border-t border-dark-border px-3 pb-3 pt-2 space-y-2">
                              <textarea
                                value={currentText}
                                onChange={e => setNudgeEdits(p => ({ ...p, [item.id]: e.target.value }))}
                                rows={3}
                                autoFocus
                                placeholder="Write a warm reminder message for this action item…"
                                className="w-full text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-100 placeholder-dark-muted resize-none"
                              />
                              <div className="flex gap-2">
                                <button type="button" onClick={() => saveNudgeMessage(item)} disabled={isSaving}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40">
                                  {isSaving ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" onClick={() => setNudgeEditingId(null)}
                                  className="text-xs px-3 py-1.5 rounded-lg border border-dark-border text-gray-300 hover:text-white hover:border-gray-500">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {isScheduleOpen && (
                            <div className="border-t border-dark-border px-3 pb-3 pt-2 space-y-2 text-sm">
                              <div className="flex gap-3">
                                {(['one_off', 'recurring'] as const).map(t => (
                                  <label key={t} className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-300">
                                    <input type="radio" checked={nudgeScheduleType === t} onChange={() => setNudgeScheduleType(t)} />
                                    {t === 'one_off' ? 'One-off' : 'Recurring'}
                                  </label>
                                ))}
                              </div>
                              {nudgeScheduleType === 'one_off' ? (
                                <input type="date" value={nudgeScheduleDate} min={nudgeMinDate()} max={nudgeMaxDate()}
                                  onChange={e => setNudgeScheduleDate(e.target.value)}
                                  className="border border-dark-border rounded px-2 py-1 text-sm bg-dark-canvas text-gray-100 [color-scheme:dark]" />
                              ) : (
                                <div className="flex gap-1 flex-wrap">
                                  {[1,2,3,4,5,6,7].map(d => (
                                    <button key={d} type="button"
                                      onClick={() => setNudgeScheduleDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort())}
                                      className={`px-2 py-0.5 rounded text-xs border ${nudgeScheduleDays.includes(d) ? 'bg-indigo-600 text-white border-indigo-600' : 'border-dark-border text-gray-300 hover:text-white'}`}>
                                      {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][d-1]}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <div className="flex gap-2 items-center">
                                <select value={nudgeScheduleHour} onChange={e => setNudgeScheduleHour(Number(e.target.value))}
                                  className="border border-dark-border rounded px-2 py-1 text-sm bg-dark-canvas text-gray-100">
                                  {Array.from({length: 24}, (_, i) => i).map(h => (
                                    <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>
                                  ))}
                                </select>
                                <select value={nudgeScheduleMinute} onChange={e => setNudgeScheduleMinute(Number(e.target.value))}
                                  className="border border-dark-border rounded px-2 py-1 text-sm bg-dark-canvas text-gray-100">
                                  <option value={0}>:00</option>
                                  <option value={30}>:30</option>
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => submitNudgeSchedule(item)}
                                  disabled={isScheduling || (nudgeScheduleType === 'one_off' ? !nudgeScheduleDate : nudgeScheduleDays.length === 0)}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
                                  {isScheduling ? 'Scheduling…' : 'Confirm'}
                                </button>
                                <button type="button" onClick={() => setOpenScheduleId(null)}
                                  className="text-xs px-3 py-1.5 rounded-lg border border-dark-border text-gray-300 hover:text-white hover:border-gray-500">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* ── Summary-dependent tabs (overview, coach notes, ef themes, client notes) ── */}
            {activeTab !== 'prep' && activeTab !== 'action_items' && activeTab !== 'nudges' && (
              <>
                {loading && <Spinner size="md" className="py-12" />}
                {!loading && error && !isCoach && (
                  <EmptyState icon={ClipboardDocumentIcon} title="No summary available" description={error} />
                )}
                {/* For coaches: when there's an error (no summary) OR no summary yet, show editable empty state */}
                {!loading && isCoach && !summary && (
                  <>
                    {activeTab === 'overview' && (
                      <div>
                        {editingOverview ? (
                          <div className="space-y-3">
                            <textarea
                              value={overviewDraft}
                              onChange={e => setOverviewDraft(e.target.value)}
                              rows={12}
                              autoFocus
                              placeholder="Write your session notes here…"
                              className="w-full text-sm text-gray-900 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                            />
                            <div className="flex justify-end gap-2">
                              <button onClick={() => setEditingOverview(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                              <button onClick={saveOverview} disabled={overviewSaving} className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                                {overviewSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-sm text-gray-500 mb-3">No notes yet for this session.</p>
                            <button onClick={() => { setOverviewDraft(''); setEditingOverview(true); }}
                              className="text-xs px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700">
                              Start session notes
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {activeTab === 'coach_notes' && (
                      <div>
                        {editingNotes ? (
                          <div className="space-y-3">
                            <textarea
                              value={notesDraft}
                              onChange={e => setNotesDraft(e.target.value)}
                              rows={10}
                              autoFocus
                              placeholder="Add your private notes about this session…"
                              className="w-full text-sm text-gray-900 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                            />
                            <div className="flex justify-end gap-2">
                              <button onClick={() => setEditingNotes(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                              <button onClick={saveNotes} disabled={notesSaving} className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                                {notesSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-sm text-gray-500 mb-3">No private notes yet for this session.</p>
                            <button onClick={() => { setNotesDraft(''); setEditingNotes(true); }}
                              className="text-xs px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700">
                              Add coach notes
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {activeTab === 'ef_themes' && (
                      <div>
                        {editingThemes ? (
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600">Toggle the EF domains active in this session:</p>
                            <div className="flex flex-wrap gap-2">
                              {ALL_EF_DOMAINS.map((theme) => {
                                const active = themesDraft.includes(theme);
                                return (
                                  <button key={theme} type="button" onClick={() => toggleTheme(theme)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize transition-opacity border-2 ${active ? `${EF_THEME_COLORS[theme]} border-transparent` : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                                    {theme}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="flex justify-end gap-2 mt-3">
                              <button onClick={() => setEditingThemes(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                              <button onClick={saveThemes} disabled={themesSaving} className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                                {themesSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-sm text-gray-500 mb-3">No EF themes identified yet.</p>
                            <button onClick={() => { setThemesDraft([]); setEditingThemes(true); }}
                              className="text-xs px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700">
                              Add EF themes
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                {!loading && !error && summary && (
                  <>
                    {/* ── Overview ── */}
                    {activeTab === 'overview' && (
                      <div>
                        {editingOverview ? (
                          <div className="space-y-3">
                            <textarea
                              value={overviewDraft}
                              onChange={e => setOverviewDraft(e.target.value)}
                              rows={12}
                              className="w-full text-sm text-gray-900 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                            />
                            <div className="flex justify-end gap-2">
                              <button onClick={() => setEditingOverview(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                              <button onClick={saveOverview} disabled={overviewSaving} className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                                {overviewSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <SummaryMarkdown
                            text={summary.summary_text}
                            clientHidden={isCoach ? clientHidden : undefined}
                            customSections={isCoach ? customSections : undefined}
                          />
                        )}
                      </div>
                    )}

                    {/* ── Coach Notes ── */}
                    {activeTab === 'coach_notes' && isCoach && (
                      <div>
                        {editingNotes ? (
                          <div className="space-y-3">
                            <textarea
                              value={notesDraft}
                              onChange={e => setNotesDraft(e.target.value)}
                              rows={10}
                              placeholder="Add your private notes about this session…"
                              className="w-full text-sm text-gray-900 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                            />
                            <div className="flex justify-end gap-2">
                              <button onClick={() => setEditingNotes(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                              <button onClick={saveNotes} disabled={notesSaving} className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                                {notesSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : summary.coach_notes ? (
                          <SummaryMarkdown text={summary.coach_notes} />
                        ) : (
                          <p className="text-gray-400 italic text-sm">No coach notes for this session. Click Edit to add some.</p>
                        )}
                      </div>
                    )}

                    {/* ── Client Notes ── */}
                    {activeTab === 'client_notes' && isCoach && (
                      <div className="space-y-6">
                        {(summary.client_sections ?? []).map((section: ClientSection, idx: number) => (
                          <div key={idx}>
                            <h4 className="text-sm font-semibold text-violet-700 mb-2">{section.heading}</h4>
                            <SummaryMarkdown text={section.content} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── EF Themes ── */}
                    {activeTab === 'ef_themes' && (
                      <div>
                        {editingThemes ? (
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600">Toggle the EF domains active in this session:</p>
                            <div className="flex flex-wrap gap-2">
                              {ALL_EF_DOMAINS.map((theme) => {
                                const active = themesDraft.includes(theme);
                                return (
                                  <button key={theme} type="button" onClick={() => toggleTheme(theme)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize transition-opacity border-2 ${active ? `${EF_THEME_COLORS[theme]} border-transparent` : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                                    {theme}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="flex justify-end gap-2 mt-3">
                              <button onClick={() => setEditingThemes(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                              <button onClick={saveThemes} disabled={themesSaving} className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                                {themesSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : summary.ef_themes.length > 0 ? (
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                              {isCoach ? 'EF domains that were active in this session:' : 'Skills you worked on in this session:'}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {summary.ef_themes.map((theme: string) => (
                                <span key={theme}
                                  className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${EF_THEME_COLORS[theme.toLowerCase()] || 'bg-gray-100 text-gray-700'}`}>
                                  {theme}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-400 italic text-sm">
                            {isCoach ? 'No EF themes identified. Click Edit to add some.' : 'No EF themes identified for this session.'}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50">
            <div className="flex items-center justify-between">
              {isCoach && !loading && !error && summary ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Helpful?</span>
                  <button
                    onClick={() => handleRate(1)}
                    disabled={ratingLoading}
                    aria-label="Thumbs up"
                    title="Accurate and useful"
                    className={`p-1.5 rounded-lg transition-colors ${rating === 1 ? 'bg-green-100 text-green-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                  >
                    <HandThumbUpIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleRate(-1)}
                    disabled={ratingLoading}
                    aria-label="Thumbs down"
                    title="Inaccurate or unhelpful"
                    className={`p-1.5 rounded-lg transition-colors ${rating === -1 ? 'bg-red-100 text-red-500' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                  >
                    <HandThumbDownIcon className="w-5 h-5" />
                  </button>
                  {ratingConfirmed && <span className="text-xs text-green-600 font-medium">Saved</span>}
                </div>
              ) : (
                <div />
              )}

              <div className="flex gap-1 items-center">
                {isCoach && reprocessError && (
                  <span className="text-xs text-red-500 mr-2">{reprocessError}</span>
                )}
                {isCoach && coachClientId && onNavigateToCheckIns && (
                  <button
                    onClick={onNavigateToCheckIns}
                    className="text-xs text-violet-600 hover:text-violet-800 hover:underline transition-colors mr-2"
                  >
                    Set up tracker →
                  </button>
                )}
                {/* Export PDF */}
                {!loading && !error && summary && (
                  <Tooltip text="Export PDF" position="bottom">
                    <button
                      onClick={handleExportPdf}
                      className="p-2 rounded-lg text-gray-500 hover:text-green-600 hover:bg-gray-100 transition-colors"
                    >
                      <ArrowDownTrayIcon className="w-5 h-5" />
                    </button>
                  </Tooltip>
                )}
                {/* Edit current tab — available for coaches even when no summary yet */}
                {isCoach && !loading && (() => {
                  const editableTab = (activeTab === 'overview' && !editingOverview)
                    || (activeTab === 'coach_notes' && !editingNotes)
                    || (activeTab === 'ef_themes' && !editingThemes);
                  if (!editableTab) return null;
                  const handler = activeTab === 'overview' ? startEditOverview
                    : activeTab === 'coach_notes' ? startEditNotes
                    : startEditThemes;
                  return (
                    <button
                      onClick={handler}
                      title="Edit"
                      className="p-2 rounded-lg text-gray-500 hover:text-violet-600 hover:bg-gray-100 transition-colors"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                  );
                })()}
                {/* Regenerate from transcript */}
                {isCoach && !loading && summary?.transcript_text && (
                  showReprocessConfirm ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">Overwrite notes?</span>
                      <button onClick={handleReprocess} className="text-xs font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded border border-red-300 hover:bg-red-50">Yes</button>
                      <button onClick={() => setShowReprocessConfirm(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">No</button>
                    </div>
                  ) : (
                    <Tooltip text="Regenerate AI summary" position="bottom">
                      <button
                        onClick={() => setShowReprocessConfirm(true)}
                        disabled={reprocessing}
                        className="p-2 rounded-lg text-gray-500 hover:text-orange-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                      >
                        <ArrowPathIcon className={`w-5 h-5 ${reprocessing ? 'animate-spin' : ''}`} />
                      </button>
                    </Tooltip>
                  )
                )}
                {/* Approve notes */}
                {isCoach && !loading && !error && summary && (
                  <Tooltip text={approvedNotes ? 'Revoke client access' : 'Share notes with client'} position="bottom">
                    <button
                      onClick={handleApproveNotes}
                      disabled={approvingNotes}
                      className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                        approvedNotes
                          ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                          : 'text-gray-500 hover:text-green-600 hover:bg-gray-100'
                      }`}
                    >
                      {approvedNotes ? <CheckCircleIcon className="w-5 h-5" /> : <EyeSlashIcon className="w-5 h-5" />}
                    </button>
                  </Tooltip>
                )}
                {/* Send notes to client */}
                {isCoach && !loading && !error && summary && approvedNotes && (
                  <div className="relative group">
                    <Tooltip text={notesSent ? 'Notes sent' : 'Send notes to client'} position="bottom">
                    <button
                      onClick={handleSendNotes}
                      disabled={sendingNotes}
                      className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                        notesSent
                          ? 'text-blue-500 hover:text-blue-600 hover:bg-blue-50'
                          : 'text-gray-500 hover:text-blue-500 hover:bg-gray-100'
                      }`}
                    >
                      {notesSent
                        ? <CheckCircleIcon className="w-5 h-5" />
                        : <PaperAirplaneIcon className="w-5 h-5" />}
                    </button>
                    </Tooltip>
                    {sendNotesError && (
                      <div className="absolute right-0 top-10 z-10 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-2 w-48 shadow">
                        {sendNotesError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SessionSummaryPanel;
